import { type ColorToken, type MenuItem } from "./types";
import {
  FiGrid, FiSettings, FiUsers, FiShield, FiTruck, FiPackage, FiMap, FiLayers, FiClipboard, FiTag, FiLink,
  FiMail, FiDollarSign
} from "react-icons/fi";
import { MdFlightTakeoff, MdOutlineHotel } from "react-icons/md";
// import { FaPlane, FaShip } from "react-icons/fa";
// import type { ReactNode } from "react";
// import { env } from "@/lib/env";
// import { LOGO } from "@/components/auth/assets";


/** Section → color map (kept exactly as provided) */
export const COLOR_MAP: Record<string, ColorToken> = {
  "Dashboard Overview": { badge: "bg-sky-100",      icon: "text-sky-600",      activeBg: "bg-sky-50",      activeText: "text-sky-700",      rail: "bg-sky-500" },
  "System Pages":       { badge: "bg-zinc-100",     icon: "text-zinc-700",     activeBg: "bg-zinc-50",     activeText: "text-zinc-800",     rail: "bg-zinc-500" },
  "HR":                 { badge: "bg-amber-100",    icon: "text-amber-600",    activeBg: "bg-amber-50",    activeText: "text-amber-700",    rail: "bg-amber-500" },
  "Products":           { badge: "bg-emerald-100",  icon: "text-emerald-600",  activeBg: "bg-emerald-50",  activeText: "text-emerald-700",  rail: "bg-emerald-500" },
  "Suppliers":          { badge: "bg-teal-100",     icon: "text-teal-600",     activeBg: "bg-teal-50",     activeText: "text-teal-700",     rail: "bg-teal-500" },
  "Affiliate Partner":  { badge: "bg-indigo-100",   icon: "text-indigo-600",   activeBg: "bg-indigo-50",   activeText: "text-indigo-700",   rail: "bg-indigo-500" },
  "Customers":          { badge: "bg-rose-100",     icon: "text-rose-600",     activeBg: "bg-rose-50",     activeText: "text-rose-700",     rail: "bg-rose-500" },
  "Tourism Board":      { badge: "bg-fuchsia-100",  icon: "text-fuchsia-600",  activeBg: "bg-fuchsia-50",  activeText: "text-fuchsia-700",  rail: "bg-fuchsia-500" },
  "Leads":              { badge: "bg-orange-100",   icon: "text-orange-600",   activeBg: "bg-orange-50",   activeText: "text-orange-700",   rail: "bg-orange-500" },
  "Guides":             { badge: "bg-cyan-100",     icon: "text-cyan-600",     activeBg: "bg-cyan-50",     activeText: "text-cyan-700",     rail: "bg-cyan-500" },
  "Tour":               { badge: "bg-sky-100",      icon: "text-sky-600",      activeBg: "bg-sky-50",      activeText: "text-sky-700",      rail: "bg-sky-500" },
  "Hotels":             { badge: "bg-emerald-100",  icon: "text-emerald-600",  activeBg: "bg-emerald-50",  activeText: "text-emerald-700",  rail: "bg-emerald-500" },
  "Transportation":     { badge: "bg-blue-100",     icon: "text-blue-600",     activeBg: "bg-blue-50",     activeText: "text-blue-700",     rail: "bg-blue-500" },
  "Itineraries":        { badge: "bg-purple-100",   icon: "text-purple-600",   activeBg: "bg-purple-50",   activeText: "text-purple-700",   rail: "bg-purple-500" },
  "Bookings":           { badge: "bg-stone-100",    icon: "text-stone-600",    activeBg: "bg-stone-50",    activeText: "text-stone-700",    rail: "bg-stone-500" },
  "Flights":            { badge: "bg-blue-100",     icon: "text-blue-600",     activeBg: "bg-blue-50",     activeText: "text-blue-700",     rail: "bg-blue-500" },
  "Settings":           { badge: "bg-zinc-100",     icon: "text-zinc-700",     activeBg: "bg-zinc-50",     activeText: "text-zinc-800",     rail: "bg-zinc-500" },
  "Cruise":             { badge: "bg-teal-100",     icon: "text-teal-600",     activeBg: "bg-teal-50",     activeText: "text-teal-700",     rail: "bg-teal-500" },

  // Links that used to sit inside the Tour / Bookings / Flights groups and
  // inherited the group's colour. Now that they are top-level under a
  // sub-heading, they need their own entry to keep it.
  "Tour Types":         { badge: "bg-sky-100",      icon: "text-sky-600",      activeBg: "bg-sky-50",      activeText: "text-sky-700",      rail: "bg-sky-500" },
  "Tour Packages":      { badge: "bg-sky-100",      icon: "text-sky-600",      activeBg: "bg-sky-50",      activeText: "text-sky-700",      rail: "bg-sky-500" },
  "Tour Bookings":      { badge: "bg-stone-100",    icon: "text-stone-600",    activeBg: "bg-stone-50",    activeText: "text-stone-800",    rail: "bg-stone-500" },
  "Flight Bookings":    { badge: "bg-stone-100",    icon: "text-stone-600",    activeBg: "bg-stone-50",    activeText: "text-stone-800",    rail: "bg-stone-500" },
  "Hotel Catalogue":    { badge: "bg-emerald-100",  icon: "text-emerald-600",  activeBg: "bg-emerald-50",  activeText: "text-emerald-700",  rail: "bg-emerald-500" },
  "Hotel Bookings":     { badge: "bg-stone-100",    icon: "text-stone-600",    activeBg: "bg-stone-50",    activeText: "text-stone-800",    rail: "bg-stone-500" },
  "Airlines":           { badge: "bg-blue-100",     icon: "text-blue-600",     activeBg: "bg-blue-50",     activeText: "text-blue-700",     rail: "bg-blue-500" },
  "Airports":           { badge: "bg-blue-100",     icon: "text-blue-600",     activeBg: "bg-blue-50",     activeText: "text-blue-700",     rail: "bg-blue-500" },
  "Activity Logs":      { badge: "bg-blue-100",     icon: "text-blue-600",     activeBg: "bg-blue-50",     activeText: "text-blue-700",     rail: "bg-blue-500" },

  // The menu label is plural; the singular key below never matched it.
  "Affiliate Partners": { badge: "bg-indigo-100",   icon: "text-indigo-600",   activeBg: "bg-indigo-50",   activeText: "text-indigo-700",   rail: "bg-indigo-500" },
};

/** Utility: find a color token for a label or null */
export const colorKeyFor = (label: string): keyof typeof COLOR_MAP | null =>
  Object.prototype.hasOwnProperty.call(COLOR_MAP, label) ? (label as keyof typeof COLOR_MAP) : null;

/** Full menu */
export const MENU: MenuItem[] = [
  { type: "link", label: "Dashboard Overview", icon: <FiGrid />, to: "/dashboard" },
  { type: "divider" },

  {
    type: "group",
    label: "HR",
    icon: <FiUsers />,
    children: [
      { type: "link", label: "Users",            icon: <FiUsers />,  to: "/hr/users" },
      { type: "link", label: "User Permissions", icon: <FiShield />, to: "/hr/permissions" },
    ],
  },
  { type: "divider" },
  { type: "label", label: "Operations", variant: "section" },

  // Sub-headings instead of collapsible groups: every screen stays one click
  // away, and the headings do the grouping the folding used to do.
  // Order follows the work flow — what we sell, who wants it, what they booked,
  // then the reference data and partners behind it.
  { type: "label", label: "Catalogue", variant: "sub" },
  { type: "link", label: "Products", icon: <FiPackage />, to: "/products" },
  { type: "link", label: "Tour Types", icon: <FiMap />, to: "/tour/tour-types" },
  { type: "link", label: "Tour Packages", icon: <FiMap />, to: "/tour/tour-packages" },
  { type: "link", label: "Hotel Catalogue", icon: <MdOutlineHotel />, to: "/hotel-catalogue" },

  { type: "label", label: "Sales", variant: "sub" },
  { type: "link", label: "Leads", icon: <FiClipboard />, to: "/leads" },
  { type: "link", label: "Customers", icon: <FiUsers />, to: "/customers" },
  { type: "link", label: "Promos", icon: <FiTag />, to: "/promos" },
  { type: "link", label: "Payment Links", icon: <FiLink />, to: "/payment-links" },

  { type: "label", label: "Bookings", variant: "sub" },
  { type: "link", label: "Tour Bookings", icon: <FiClipboard />, to: "/bookings" },
  { type: "link", label: "Flight Bookings", icon: <MdFlightTakeoff />, to: "/flight-bookings" },
  { type: "link", label: "Hotel Bookings", icon: <MdOutlineHotel />, to: "/hotel-bookings" },

  { type: "label", label: "Flights", variant: "sub" },
  { type: "link", label: "Airlines", icon: <MdFlightTakeoff />, to: "/flights/airlines" },
  { type: "link", label: "Airports", icon: <FiMap />, to: "/flights/airports" },

  { type: "label", label: "Partners", variant: "sub" },
  { type: "link", label: "Suppliers", icon: <FiTruck />, to: "/suppliers" },
  { type: "link", label: "Affiliate Partners", icon: <FiUsers />, to: "/affiliates" },
  { type: "divider" },
  { type: "label", label: "Configuration", variant: "section" },
  {
    type: "group",
    label: "Settings",
    icon: <FiSettings />,
    // Ordered by what it governs: access control first (Roles grant Modules),
    // then outbound comms, then the reference data the rest of the app reads.
    // Kept as a flat list — a nested group inside a group buries links, and the
    // RBAC filter can strand a section heading whose links are all hidden.
    children: [
      { type: "link", label: "Roles",           icon: <FiShield />,     to: "/roles" },
      { type: "link", label: "Modules",         icon: <FiLayers />,     to: "/modules" },
      { type: "link", label: "Email Templates", icon: <FiMail />,       to: "/email-templates" },
      { type: "link", label: "Currencies",      icon: <FiDollarSign />, to: "/currencies" },
      // Countries / Regions / Cities are tabs inside this page, not separate links.
      { type: "link", label: "Locations",       icon: <FiMap />,        to: "/locations" },
      // Read-only diagnostics rather than configuration, so it sits last.
      { type: "link", label: "Activity Logs",   icon: <FiClipboard />,  to: "/flights/activity-logs" },
    ],
  },

  // { type: "link", label: "Flights", icon: <FaPlane />, to: "/flights", disabled: true },
  // { type: "link", label: "Cruise",  icon: <FaShip />,  to: "/cruise",  disabled: true },

];
